import { useCallback, useEffect, useState } from "react";
import { useSSE } from "react-hooks-sse";

import { JobsApi } from "@common/api/v1.1/actions/JobsApi";
import { isJobEvent, ListedJob } from "@common/api/v1.1/types/jobs";
import { Actions, State } from "../../../types";
import { ApiList } from "../../../../../api/v1.1/types/common";

export enum JobListState {
  failed,
  fetching,
  ready,
}
type JobListStateResponse = [
  JobListState, // current state
  Array<string>, // list of job IDs for the current page
  number, // the current page number
  number, // the total number of pages
  number, // the total nubmer of jobs
  string, // the current text filter
  () => void, // nextPage callback
  () => void, // previousPage callback
  () => void, // retry failed page callback
  (text: string) => void, // callback to update text filter
];

const JOBS_PER_PAGE = 10;

const fetchJobPage = async (
  // TODO : Could add some caching so that we don't refetch fully every time pagination is done
  auth: State["auth"], blockingTask: string, page: number, filter:string): Promise<ApiList<ListedJob>> => {
  const offset = JOBS_PER_PAGE * page;
  const jobsApi = new JobsApi(auth);
  const result = await jobsApi.listJobs(JOBS_PER_PAGE, offset, { 
    blockingTask, 
    status: "blocked",
    name_starts_with : filter
   });
  return result;
};

export const useJobListState = (
  auth: State["auth"],
  blockingStep: string,
  jobDatabase: State["jobs"],
  updateJobs: Actions["updateJobs"],
): JobListStateResponse => {
  const [pageNumber, setPageNumber] = useState(0);
  const [totalJobCount, setTotalJobCount] = useState(0);
  const [jobListState, setJobListState] = useState<JobListState>(JobListState.fetching);
  const [jobIds, setJobIds] = useState<Array<string>>([]);
  const [textFilter, setTextFilter] = useState("");
  const [textFilterTimeout, setTextFilterTimeout] = useState<NodeJS.Timeout>();

  useEffect(() => {
    const databaseJobIds = Object.keys(jobDatabase);
    if (databaseJobIds.length !== jobIds.length) {
      setJobIds(databaseJobIds);
    }
  }, [jobDatabase]);
  useEffect(() => {
    setPageNumber(0);
    setJobListState(JobListState.fetching);
    setJobIds([]);
  }, [auth]);
  useEffect(() => {
    if (jobListState !== JobListState.fetching) {
      return;
    }
    fetchJobPage(auth, blockingStep, 0, "")
      .then(response => {
        setTotalJobCount(response.total_count);
        updateJobs(response.list, null);
        setJobIds(response.list.map(j => j.id));
        setJobListState(JobListState.ready);
      })
      .catch(err => {
        console.error(err);
        setJobIds([]);
        setJobListState(JobListState.failed);
      });
  }, [jobListState]);
  useSSE(
    "jobEvent",
    {},
    {
      parser: input => JSON.parse(input),
      stateReducer: (state, action) => {
        if (action.data) {
          const parsedData: any = action.data;
          const { job, type } = parsedData;
          if (typeof job === "object" && job !== null && !Array.isArray(job) && isJobEvent(type)) {
            updateJobs([job], type);
            return state;
          }
          console.error("Invalid event data:", action.data);
        }
        return state;
      },
    },
  );

  const totalPages = Math.ceil(totalJobCount / JOBS_PER_PAGE);
  const nextPage = useCallback(() => {
    if (pageNumber >= totalPages - 1) {
      return;
    }
    setPageNumber(pageNumber + 1);
    refreshJobList(pageNumber + 1, textFilter);

  }, [pageNumber, setPageNumber, totalPages]);
  const prevPage = useCallback(() => {
    if (pageNumber <= 0) {
      return;
    }
    setPageNumber(pageNumber - 1);
    refreshJobList(pageNumber - 1, textFilter);
  }, [pageNumber, setPageNumber]);

  const refreshJobList = (pageNumber: number, filter: string) => {
    fetchJobPage(auth, blockingStep, pageNumber, filter)
    .then(response => {
      setTotalJobCount(response.total_count);
      updateJobs(response.list, null);
      setJobIds(response.list.map(j => j.id));
      setJobListState(JobListState.ready);
    })
    .catch(err => {
      console.error(err);
      setJobIds([]);
      setJobListState(JobListState.failed);
    });
  }

  const retry = useCallback(() => {
    setJobListState(JobListState.fetching);
  }, [setJobListState]);

  
  const setJobTextFilter = useCallback((filter: string) => {
    setTextFilter(filter);
    if (filter.length > 2 || filter.length === 0) {
      if (textFilterTimeout) {
        clearTimeout(textFilterTimeout)
      }
      const timeout = setTimeout(() => {
        setPageNumber(0);
        refreshJobList(0, filter);
      }, 300);
      setTextFilterTimeout(timeout);
    }
  }, [textFilter, setTextFilter]);

  return [
    jobListState,
    jobIds,
    pageNumber,
    totalPages,
    totalJobCount,
    textFilter,
    nextPage,
    prevPage,
    retry,
    setJobTextFilter,
  ];
};

